import React, { useState, useEffect, useRef } from 'react';
import supabase from '../../supabase/supabase';
import { FiFileText, FiSave, FiEye, FiType, FiAlignLeft, FiAlignCenter, FiAlignRight, FiAlignJustify } from 'react-icons/fi';

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [activePage, setActivePage] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formatMessage, setFormatMessage] = useState('');
  const editorRef = useRef(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    const { data } = await supabase
      .from('site_pages')
      .select('*')
      .order('title');
    if (data) {
      setPages(data);
      if (data.length > 0 && !activePage) {
        selectPage(data[0]);
      }
    }
  };

  const selectPage = (page) => {
    setActivePage(page);
    setTitle(page.title);
    setContent(page.content || '');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!activePage) return;
    setSaving(true);

    const { error } = await supabase
      .from('site_pages')
      .update({ title, content, updated_at: new Date().toISOString() })
      .eq('id', activePage.id);

    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      fetchPages();
    }
    setSaving(false);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    setSaved(false);
  };

  const insertAtCursor = (insertion) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setContent(c => c + insertion);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + insertion + content.substring(end);
    setContent(newContent);
    setSaved(false);
  };

  const wrapSelection = (before, after) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    const wrapped = before + (selected || 'text') + after;
    const newContent = content.substring(0, start) + wrapped + content.substring(end);
    setContent(newContent);
    setSaved(false);
  };

  const insertTag = (tag) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    let insertion = '';

    switch (tag) {
      case 'h2': insertion = `<h2>${selected || 'Heading'}</h2>`; break;
      case 'h3': insertion = `<h3>${selected || 'Subheading'}</h3>`; break;
      case 'h4': insertion = `<h4>${selected || 'Small Heading'}</h4>`; break;
      case 'p': insertion = `<p>${selected || 'Paragraph text'}</p>`; break;
      case 'ul': insertion = `<ul>\n  <li>${selected || 'List item'}</li>\n</ul>`; break;
      case 'ol': insertion = `<ol>\n  <li>${selected || 'List item'}</li>\n</ol>`; break;
      case 'strong': wrapSelection('<strong>', '</strong>'); return;
      case 'em': wrapSelection('<em>', '</em>'); return;
      case 'u': wrapSelection('<u>', '</u>'); return;
      case 'a': insertion = `<a href="https://">${selected || 'Link text'}</a>`; break;
      case 'br': insertion = '<br />'; break;
      case 'hr': insertion = '<hr />'; break;
      case 'align-left': wrapSelection('<div style="text-align:left;">', '</div>'); return;
      case 'align-center': wrapSelection('<div style="text-align:center;">', '</div>'); return;
      case 'align-right': wrapSelection('<div style="text-align:right;">', '</div>'); return;
      case 'align-justify': wrapSelection('<div style="text-align:justify;">', '</div>'); return;
      case 'highlight': wrapSelection('<mark style="background:#fef3c7;padding:2px 6px;border-radius:4px;">', '</mark>'); return;
      case 'small': wrapSelection('<small>', '</small>'); return;
      case 'blockquote': insertion = `<blockquote>${selected || 'Quote text'}</blockquote>`; break;
      case 'callout': insertion = `<div style="background:#faf9f6;border-left:4px solid #d4af37;padding:20px 24px;margin:24px 0;border-radius:0 12px 12px 0;"><p style="margin:0;color:#4b5563;">${selected || 'Important note'}</p></div>`; break;
      default: break;
    }

    insertAtCursor(insertion);
  };

  const autoFormat = () => {
    const text = content.trim();
    if (!text) {
      setFormatMessage('Nothing to format');
      setTimeout(() => setFormatMessage(''), 2000);
      return;
    }

    const hasHtml = /<[a-z][\s\S]*>/i.test(text);
    if (hasHtml) {
      setFormatMessage('Content already contains HTML. Clear it first or paste plain text.');
      setTimeout(() => setFormatMessage(''), 3000);
      return;
    }

    const lines = text.split('\n');
    let formatted = '';
    let inList = false;
    let listType = '';

    const closeList = () => {
      if (inList) {
        formatted += listType === 'ul' ? '</ul>' : '</ol>';
        inList = false;
        listType = '';
      }
    };

    const openList = (type) => {
      if (!inList || listType !== type) {
        closeList();
        formatted += type === 'ul' ? '<ul>' : '<ol>';
        inList = true;
        listType = type;
      }
    };

    const inlineFormat = (str) => {
      return str
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1">$1</a>')
        .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1">$1</a>');
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      if (!trimmed) {
        closeList();
        return;
      }

      const bulletMatch = trimmed.match(/^[–\-*•]\s+(.+)/);
      if (bulletMatch) {
        openList('ul');
        formatted += `<li>${inlineFormat(bulletMatch[1])}</li>`;
        return;
      }

      const subSectionMatch = trimmed.match(/^(\d+\.\d+)\.\s+(.+)/);
      if (subSectionMatch) {
        closeList();
        formatted += `<p><strong>${subSectionMatch[1]}.</strong> ${inlineFormat(subSectionMatch[2])}</p>`;
        return;
      }

      const sectionMatch = trimmed.match(/^(\d+)\.\s+([A-Z][^.]{2,50})$/);
      if (sectionMatch) {
        closeList();
        formatted += `<h3>${sectionMatch[1]}. ${sectionMatch[2]}</h3>`;
        return;
      }

      if (trimmed.startsWith('# ')) {
        closeList();
        formatted += `<h2>${inlineFormat(trimmed.substring(2))}</h2>`;
        return;
      }
      if (trimmed.startsWith('## ')) {
        closeList();
        formatted += `<h3>${inlineFormat(trimmed.substring(3))}</h3>`;
        return;
      }

      if (
        trimmed.length < 50 &&
        !trimmed.endsWith('.') &&
        !trimmed.endsWith(',') &&
        !trimmed.match(/^\d/) &&
        /^[A-Z][a-zA-Z\s&]+$/.test(trimmed) &&
        trimmed.split(/\s+/).length <= 5
      ) {
        closeList();
        if (index < 3) {
          formatted += `<h2>${trimmed}</h2>`;
        } else {
          formatted += `<h3>${trimmed}</h3>`;
        }
        return;
      }

      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
      if (numberedMatch && numberedMatch[2].length > 50) {
        closeList();
        formatted += `<p><strong>${numberedMatch[1]}.</strong> ${inlineFormat(numberedMatch[2])}</p>`;
        return;
      }
      if (numberedMatch && numberedMatch[2].endsWith('.')) {
        openList('ol');
        formatted += `<li>${inlineFormat(numberedMatch[2])}</li>`;
        return;
      }

      closeList();
      formatted += `<p>${inlineFormat(trimmed)}</p>`;
    });

    closeList();

    setContent(formatted);
    setSaved(false);
    setFormatMessage('✓ Plain text formatted to HTML!');
    setTimeout(() => setFormatMessage(''), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-gray-800">Site Pages</h2>
          <p className="text-sm text-gray-500 mt-1">Manage Terms & Conditions, Privacy Policy, and other legal pages.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page List */}
        <div className="lg:col-span-1 space-y-2">
          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Pages</h3>
            {pages.map(page => (
              <button
                key={page.id}
                onClick={() => selectPage(page)}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all mb-1 flex items-center gap-2 ${
                  activePage?.id === page.id
                    ? 'bg-brand-darkSage text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FiFileText size={14} className={activePage?.id === page.id ? 'text-brand-gold' : 'text-gray-400'} />
                {page.title}
              </button>
            ))}
          </div>

          {activePage && (
            <a
              href={`/page/${activePage.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-brand-gold hover:text-brand-darkSage transition-colors px-4 py-2"
            >
              <FiEye size={14} /> View Live Page
            </a>
          )}
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {activePage ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Title */}
              <div className="p-6 border-b border-gray-100">
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Page Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setSaved(false); }}
                  className="w-full text-xl font-serif font-bold text-gray-800 border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none"
                />
              </div>

              {/* Enhanced Toolbar */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 space-y-2">
                {/* Row 1: Structure */}
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-300 mr-1">Structure</span>
                  <ToolbarBtn label="H2" onClick={() => insertTag('h2')} />
                  <ToolbarBtn label="H3" onClick={() => insertTag('h3')} />
                  <ToolbarBtn label="H4" onClick={() => insertTag('h4')} />
                  <ToolbarBtn label="¶" onClick={() => insertTag('p')} title="Paragraph" />
                  <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
                  <ToolbarBtn label="• List" onClick={() => insertTag('ul')} />
                  <ToolbarBtn label="1. List" onClick={() => insertTag('ol')} />
                  <ToolbarBtn label="Quote" onClick={() => insertTag('blockquote')} />
                  <ToolbarBtn label="Link" onClick={() => insertTag('a')} />
                  <ToolbarBtn label="Line" onClick={() => insertTag('hr')} />
                  <ToolbarBtn label="Break" onClick={() => insertTag('br')} />
                </div>

                {/* Row 2: Text + Alignment */}
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-300 mr-1">Text</span>
                  <ToolbarBtn label="B" onClick={() => insertTag('strong')} bold />
                  <ToolbarBtn label="I" onClick={() => insertTag('em')} italic />
                  <ToolbarBtn label="U" onClick={() => insertTag('u')} underline />
                  <ToolbarBtn label="Small" onClick={() => insertTag('small')} />
                  <ToolbarBtn label="Highlight" onClick={() => insertTag('highlight')} />
                  <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-300 mr-1">Align</span>
                  <ToolbarBtn icon={<FiAlignLeft size={13} />} onClick={() => insertTag('align-left')} title="Left" />
                  <ToolbarBtn icon={<FiAlignCenter size={13} />} onClick={() => insertTag('align-center')} title="Center" />
                  <ToolbarBtn icon={<FiAlignRight size={13} />} onClick={() => insertTag('align-right')} title="Right" />
                  <ToolbarBtn icon={<FiAlignJustify size={13} />} onClick={() => insertTag('align-justify')} title="Justify" />
                  <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
                  <ToolbarBtn label="💡 Callout" onClick={() => insertTag('callout')} />
                  <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
                  <button
                    type="button"
                    onClick={autoFormat}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-brand-gold/10 border border-brand-gold/30 text-brand-gold hover:bg-brand-gold hover:text-white transition-all font-bold"
                  >
                    <FiType size={12} />
                    Auto-Format
                  </button>
                </div>
              </div>

              {/* Format Message */}
              {formatMessage && (
                <div className="px-6 py-2 bg-brand-gold/5 border-b border-brand-gold/10">
                  <p className="text-xs font-medium text-brand-gold">{formatMessage}</p>
                </div>
              )}

              {/* Content Editor */}
              <div className="p-6">
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Content (HTML)</label>
                <textarea
                  ref={editorRef}
                  value={content}
                  onChange={handleContentChange}
                  rows={24}
                  className="w-full font-mono text-sm border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none resize-y leading-relaxed"
                  placeholder="Paste plain text here and click Auto-Format, or write HTML directly..."
                />
                <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 font-semibold mb-2">Auto-Format Guide:</p>
                  <ul className="text-xs text-gray-400 space-y-1">
                    <li>• <strong>Section headings</strong> like "1. Introduction" → H3 headings</li>
                    <li>• <strong>Sub-sections</strong> like "1.1. The following..." → Bold prefix paragraphs</li>
                    <li>• <strong>Bullet items</strong> starting with –, -, *, • → Bullet lists</li>
                    <li>• <strong>Short standalone lines</strong> like "Contact Us" → Headings</li>
                    <li>• <strong>Email addresses</strong> and <strong>URLs</strong> → Auto-linked</li>
                    <li>• <strong>**bold**</strong> and <strong>*italic*</strong> → Formatted text</li>
                  </ul>
                </div>
              </div>

              {/* Preview */}
              <div className="p-6 border-t border-gray-100">
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Preview</label>
                <div
                  className="blog-content border border-gray-100 rounded-xl p-6 bg-gray-50/30 min-h-[200px]"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>

              {/* Save */}
              <div className="p-6 border-t border-gray-100 flex items-center gap-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-brand-darkSage text-white px-8 py-3 rounded-xl text-sm font-bold uppercase tracking-widest hover:bg-brand-gold transition-all disabled:opacity-50 shadow-lg"
                >
                  <FiSave size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {saved && (
                  <span className="text-sm font-medium text-green-600 animate-pulse">✓ Saved successfully</span>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
              <FiFileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a page to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ToolbarBtn = ({ label, icon, onClick, bold, italic, underline, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title || label}
    className={`px-2.5 py-1.5 text-[11px] rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all flex items-center gap-1 ${
      bold ? 'font-bold' : ''
    } ${italic ? 'italic' : ''} ${underline ? 'underline' : ''}`}
  >
    {icon || label}
  </button>
);

export default AdminPages;